import { useEffect, useState } from 'react'
import { useAuth } from '../../AuthProvider.ts'
import { useUpdateUser } from '../../http/hooks.ts'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { UpdateUserDto } from '../../services/nextpeakClient/endpoints/updateUser'
import { IUser } from '../../services/nextpeakClient'
import { Page, PageProps } from '../../layout/Page.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'
import { Label, MutedText, Text } from '../../newComponents/Texts/index.tsx'

const Account = () => {
	const { authUser } = useAuth()
	const queryClient = useQueryClient()
	const client = useNextPeakClient()

	const [userData, setUserData] = useState<IUser | null>(null)
	const [loading, setLoading] = useState(true)

	const [fullName, setFullName] = useState<string>('')
	const [primaryEmail, setPrimaryEmail] = useState<string>('')
	const [primaryPhone, setPrimaryPhone] = useState<string>('')
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

	const updateUserMutation = useUpdateUser(client)

	// Fetch user data using getUserV2
	useEffect(() => {
		const fetchUserData = async () => {
			if (authUser?.sub) {
				try {
					setLoading(true)
					const includes = ['organization', 'roles', 'userPosition', 'managerRelation']
					const user = await client.getUserV2(authUser.sub, includes)

					setUserData(user)

					setFullName(user.name || '')
					setPrimaryEmail(user.email || '')
					setPrimaryPhone(user.phone || '')

					//console.log("User data loaded:", user)
				} catch (error: any) {
					console.error('Error fetching user data:', error)
					toast.error('Failed to load user information')
				} finally {
					setLoading(false)
				}
			}
		}

		fetchUserData()
	}, [authUser?.sub, client])

	// Handle form submission
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (!userData?.id) {
			toast.error('User ID not found')
			return
		}

		setIsSubmitting(true)

		try {
			// Only include fields that have changed
			const changedFields: UpdateUserDto = {}

			if (fullName !== userData.name) {
				changedFields.name = fullName
			}

			if (primaryPhone !== userData.phone) {
				changedFields.primaryPhone = primaryPhone
			}

			//console.log("Changed fields being sent:", changedFields)

			// Only make the API call if there are changes
			if (Object.keys(changedFields).length > 0) {
				await updateUserMutation.mutateAsync({
					userId: userData.id,
					updateData: changedFields,
				})
				// Refresh user data
				queryClient.invalidateQueries({ queryKey: ['user', userData.id] })

				setUserData((prev) => {
					if (!prev) return null
					return {
						...prev,
						name: changedFields.name || prev.name,
						phone: changedFields.primaryPhone || prev.phone,
					}
				})
			} else {
				toast.info('No changes to save')
			}
		} catch (error: any) {
			console.error('Error updating user:', error)

			if (error.response?.data?.message) {
				toast.error(`Update failed: ${error.response.data.message}`)
			} else {
				toast.error('Failed to update profile. Please try again.')
			}
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target

		switch (name) {
			case 'fullName':
				setFullName(value)
				break
			case 'primaryPhone':
				setPrimaryPhone(value)
				break
		}
	}

	const handlePasswordChange = () => {
		//navigate to password change page ??
		console.log('Navigate to password change page')
	}

	const pageMeta: PageProps = {
		title: 'Account',
		breadcrumbs: 'Account',
	}

	if (loading) {
		return (
			<Page {...pageMeta}>
				<div className="flex items-center justify-center h-64">
					<p>Loading user information...</p>
				</div>
			</Page>
		)
	}

	return (
		<Page {...pageMeta}>
			<div className="grid grid-cols-12 gap-8">
				<div className="rounded-xl bg-white shadow-default dark:border-strokedark dark:bg-boxdark col-span-12 xl:col-span-8 p-7 flex flex-col justify-between">
					<form onSubmit={handleSubmit}>
						{/* Full Name Field */}
						<div className="mb-5.5 flex flex-col gap-5.5 sm:flex-row">
							<div className="w-full">
								<Label htmlFor="fullName">Full Name</Label>
								<div className="relative">
									<span className="absolute left-4.5 top-4">
										<svg className="fill-current" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
											<g opacity="0.8">
												<path
													fillRule="evenodd"
													clipRule="evenodd"
													d="M3.72039 12.887C4.50179 12.1056 5.5616 11.6666 6.66667 11.6666H13.3333C14.4384 11.6666 15.4982 12.1056 16.2796 12.887C17.061 13.6684 17.5 14.7282 17.5 15.8333V17.5C17.5 17.9602 17.1269 18.3333 16.6667 18.3333C16.2064 18.3333 15.8333 17.9602 15.8333 17.5V15.8333C15.8333 15.1703 15.5699 14.5344 15.1011 14.0655C14.6323 13.5967 13.9964 13.3333 13.3333 13.3333H6.66667C6.00363 13.3333 5.36774 13.5967 4.8989 14.0655C4.43006 14.5344 4.16667 15.1703 4.16667 15.8333V17.5C4.16667 17.9602 3.79357 18.3333 3.33333 18.3333C2.8731 18.3333 2.5 17.9602 2.5 17.5V15.8333C2.5 14.7282 2.93899 13.6684 3.72039 12.887Z"
													fill=""
												></path>
												<path
													fillRule="evenodd"
													clipRule="evenodd"
													d="M9.99967 3.33329C8.61896 3.33329 7.49967 4.45258 7.49967 5.83329C7.49967 7.214 8.61896 8.33329 9.99967 8.33329C11.3804 8.33329 12.4997 7.214 12.4997 5.83329C12.4997 4.45258 11.3804 3.33329 9.99967 3.33329ZM5.83301 5.83329C5.83301 3.53211 7.69849 1.66663 9.99967 1.66663C12.3009 1.66663 14.1663 3.53211 14.1663 5.83329C14.1663 8.13448 12.3009 9.99996 9.99967 9.99996C7.69849 9.99996 5.83301 8.13448 5.83301 5.83329Z"
													fill=""
												></path>
											</g>
										</svg>
									</span>
									<input
										className="w-full rounded border border-stroke bg-gray py-3 pl-11.5 pr-4.5 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
										type="text"
										name="fullName"
										id="fullName"
										placeholder="Enter your full name"
										value={fullName}
										onChange={handleInputChange}
									/>
								</div>
							</div>
						</div>

						{/* Email Field - made read-only */}
						<div className="mb-5.5">
						<Label htmlFor="primaryEmail" >
								Email Address
							</Label>
							<div className="relative">
								<span className="absolute left-4.5 top-4">
									<svg className="fill-current" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
										<g opacity="0.8">
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M3.33301 4.16667C2.87658 4.16667 2.49967 4.54357 2.49967 5V15C2.49967 15.4564 2.87658 15.8333 3.33301 15.8333H16.6663C17.1228 15.8333 17.4997 15.4564 17.4997 15V5C17.4997 4.54357 17.1228 4.16667 16.6663 4.16667H3.33301ZM0.833008 5C0.833008 3.6231 1.9561 2.5 3.33301 2.5H16.6663C18.0432 2.5 19.1663 3.6231 19.1663 5V15C19.1663 16.3769 18.0432 17.5 16.6663 17.5H3.33301C1.9561 17.5 0.833008 16.3769 0.833008 15V5Z"
												fill=""
											></path>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M0.983719 4.52215C1.24765 4.1451 1.76726 4.05341 2.1443 4.31734L9.99975 9.81615L17.8552 4.31734C18.2322 4.05341 18.7518 4.1451 19.0158 4.52215C19.2797 4.89919 19.188 5.4188 18.811 5.68272L10.4776 11.5161C10.1907 11.7169 9.80879 11.7169 9.52186 11.5161L1.18853 5.68272C0.811486 5.4188 0.719791 4.89919 0.983719 4.52215Z"
												fill=""
											></path>
										</g>
									</svg>
								</span>
								<input
									className="w-full rounded border border-stroke bg-gray py-3 pl-11.5 pr-4.5 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary cursor-not-allowed opacity-70"
									type="email"
									name="primaryEmail"
									id="primaryEmail"
									placeholder="Enter your email"
									value={primaryEmail}
									readOnly
								/>
							</div>
						</div>

						{/* Phone Number Field */}
						<div className="mb-5.5">
						<Label htmlFor="primaryPhone">
								Phone Number
							</Label>
							<div className="relative">
								<span className="absolute left-4.5 top-4">
									<svg className="fill-current" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
										<g opacity="0.8">
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M3.33366 1.66666C3.87399 1.66666 4.30566 2.10499 4.30566 2.64532V17.3546C4.30566 17.895 3.87399 18.3333 3.33366 18.3333C2.79332 18.3333 2.36166 17.895 2.36166 17.3546V2.64532C2.36166 2.10499 2.79332 1.66666 3.33366 1.66666Z"
												fill=""
											></path>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M16.667 1.66666C17.2074 1.66666 17.639 2.10499 17.639 2.64532V17.3546C17.639 17.895 17.2074 18.3333 16.667 18.3333C16.1267 18.3333 15.695 17.895 15.695 17.3546V2.64532C15.695 2.10499 16.1267 1.66666 16.667 1.66666Z"
												fill=""
											></path>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M8.33319 3.33334C8.87353 3.33334 9.30519 3.77167 9.30519 4.31201V15.6887C9.30519 16.229 8.87353 16.6667 8.33319 16.6667C7.79286 16.6667 7.36119 16.229 7.36119 15.6887V4.31201C7.36119 3.77167 7.79286 3.33334 8.33319 3.33334Z"
												fill=""
											></path>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M11.6675 3.33334C12.2078 3.33334 12.6395 3.77167 12.6395 4.31201V15.6887C12.6395 16.229 12.2078 16.6667 11.6675 16.6667C11.1271 16.6667 10.6955 16.229 10.6955 15.6887V4.31201C10.6955 3.77167 11.1271 3.33334 11.6675 3.33334Z"
												fill=""
											></path>
										</g>
									</svg>
								</span>
								<input
									className="w-full rounded border border-stroke bg-gray py-3 pl-11.5 pr-4.5 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
									type="text"
									name="primaryPhone"
									id="primaryPhone"
									placeholder="Enter your phone number"
									value={primaryPhone}
									onChange={handleInputChange}
								/>
							</div>
						</div>

						<div className="flex justify-end gap-4.5 pt-12">
							<button
								type="button"
								className="flex justify-center rounded border border-stroke py-2 px-6 font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white"
								onClick={handlePasswordChange}
							>
								Change password
							</button>
							<button
								type="submit"
								className="flex justify-center rounded bg-primary py-2 px-6 font-medium text-gray hover:bg-opacity-90"
								disabled={isSubmitting}
							>
								{isSubmitting ? 'Saving...' : 'Save'}
							</button>
						</div>
					</form>
				</div>
				<div className="rounded-xl bg-white shadow-default dark:border-strokedark dark:bg-boxdark col-span-12 xl:col-span-4 p-4 flex flex-col items-center">
					<div className="relative z-30 mx-auto h-20 w-full max-w-20 bg-white/20 overflow-hidden flex justify-center items-center rounded-full mt-7">
						{userData?.avatar ? (
							<img src={userData.avatar} alt="profile" />
						) : (
							<img
								src="https://static.vecteezy.com/system/resources/thumbnails/003/337/584/small/default-avatar-photo-placeholder-profile-icon-vector.jpg"
								alt="profile"
							/>
						)}
					</div>
					<h5 className="text-2xl mb-2 mt-4 font-semibold text-black">{userData?.name || 'User'}</h5>

					{userData?.userPosition?.positionTrajectory?.fromPosition?.title ? (
						<p className="text-lg mb-1 text-gray-700 dark:text-gray-300">{userData.userPosition.positionTrajectory.fromPosition.title}</p>
					) : null}

					{userData?.organization?.name ? <p className="text-xl mb-2">{userData.organization.name}</p> : null}

					{/* Roles (if available) */}
					{userData?.roles && userData.roles.length > 0 ? (
						<div className="mt-2 flex flex-wrap justify-center gap-2">
							{userData.roles.map((role) => (
								<span
									key={role.id}
									className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full dark:bg-gray-700 dark:text-gray-300"
								>
									{role.name}
								</span>
							))}
						</div>
					) : null}

					{/* Manager (if available) */}
					{userData?.managerRelation?.manager ? (
						<div className="mt-3 text-sm">
							<MutedText>Manager: </MutedText>
							<Text weight='medium'>{userData.managerRelation.manager.name}</Text>
						</div>
					) : null}
				</div>
			</div>
		</Page>
	)
}

export default Account
